import React, { useContext } from 'react'

import CardContent from '@material-ui/core/CardContent'
import Avatar from '@material-ui/core/Avatar'
import Fab from '@material-ui/core/Fab'

import Tooltip from '@material-ui/core/Tooltip'

import { AvatarGroup } from '@material-ui/lab'
import { ReactFC } from 'types'
import styled from 'styled-components'
import { PostEventData } from 'posts/posts.types'

import { useSelector } from 'react-redux'
import { selectInstanceName } from 'profile/profile.selectors'
import { FormattedDate, FormattedMessage } from 'react-intl'
import { getEventStatusId } from 'events/EventControls'
import { Block } from 'pages/profile/posts/view/content/Content'
import { getDirection } from "core/useDirectionality"

import { url4post } from 'circle/places'

import { getPostSummary } from "../lib/getPostSummary"
import { getPersonName, PersonNameContext } from 'components/PersonName'
import { EventTime } from './EventTime'
import Link from '@material-ui/core/Link'

import { Link as RouterLink } from "react-router-dom"
import { CalendarIcon } from './events.icons'
import styles from "./EventList.css"

import { getEventStatusInfo } from './getEventStatusInfo'
import { postCalendarLink } from './events.location'
import PrimaryButton from 'components/PrimaryButton/PrimaryButton'
import { CalendarEvent, isExpoWrapper, useCalendar, IMAGE_CDN_ROOT } from "lib-core"

const renderEventColor = (status) => {
    if (status === "event.reportAvailable") {
        return "orange"
    }
    if (status === "event.aboutToStart") {
        return "#00bbff"
    }

    if (status === "event.joinMeeting") {
        return "#220ce7"
    }

    return "green"
}

const EventStatusWrapper = styled.div<{ status }>`
    ${({ theme }) => theme.breakpoints.down('sm')} {
    }

    background-color: ${({ status }) => renderEventColor(status)};
`

const EventStatus: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const id = getEventStatusId(event)
    return (
        <div className={styles.event_status}>
            <EventStatusWrapper status={id}>
                <FormattedMessage id={"statusShort." + id} />
            </EventStatusWrapper>
        </div>
    )
}

const AdditionalLine: ReactFC<{ text: string, url: string, disabled?: boolean }> = ({ text, url, disabled }) => {
    if (!text) {
        return null
    }

    if (url) {
        return (
            <PrimaryButton href={url} disabled={disabled} target="_blank">
                {text}
            </PrimaryButton>
        )
    }
    return (
        <PrimaryButton>
            {text}
        </PrimaryButton>
    )
}

const EventAdditional: ReactFC<{ event: PostEventData }> = ({ event }) => {
    if (!event.label) {
        return null
    }
    const { label, url } = event
    const { isFinished } = getEventStatusInfo(event)
    return (
        <div className={styles.event_info}>
            <AdditionalLine text={label} url={url} disabled={isFinished} />
        </div>
    )
}

const ImageStyled = styled.div<{ url }>`
    height: 240px;
    background-size: cover;
    background-position: center;
    position: relative;
    background-color:#000;
    background-image: url(${({ url }) => url});
    cursor: pointer;
`

const EventImage: ReactFC<{ event: PostEventData }> = ({ event, children }) => {
    const post = event.post
    const uuid = post.image?.uuid
    const instanceName = useSelector(selectInstanceName)
    const url = `${IMAGE_CDN_ROOT}${instanceName}/${uuid}?h=${240}&fit=facearea&facepad=50&auto=format`
    return (
        <Link component={RouterLink} underline="hover" to={url4post(post)}>
            <ImageStyled url={uuid && url}>
                {children}
            </ImageStyled>
        </Link>
    )
}

const DateLayer: ReactFC<{ event: PostEventData }> = ({ event }) => {
    return (
        <div className={styles.event_start_date}>
            <FormattedDate
                value={event.localStartTime}
                year="numeric"
                month="long"
                day="2-digit"
            />
        </div>
    )
}

const DownloadCalendarFab: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const onClick = (e: React.MouseEvent) => {
        e.stopPropagation()
    }
    const { dialog, createEvent } = useCalendar()
    const onCalendar = (e: React.MouseEvent) => {
        if (!isExpoWrapper()) {
            return
        }
        e.stopPropagation()
        e.preventDefault()
        const post = event.post
        const eventData: Partial<CalendarEvent> = {
            title: post.title,
            startDate: event.localStartTime,
            alarms: [
                {
                    relativeOffset: -10,
                }
            ],
            endDate: event.localEndTime,
        }
        createEvent(eventData)

    }
    const url = postCalendarLink(event.post)
    return (
        <div className={styles.event_calendar}
            onClick={onClick}
        >
            {dialog}
            <Tooltip title="Add to my calendar" aria-label="add">
                <Fab
                    style={{ backgroundColor: "white" }}
                    className={styles.fab}
                    href={url} target="_blank" download
                    onClick={onCalendar}
                >
                    <CalendarIcon size={25} />
                </Fab>
            </Tooltip>
        </div>
    )
}

const EventTitle: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const post = event.post

    return (
        <Link component={RouterLink} underline="hover" to={url4post(post)}>
            <h3 dir="auto">
                {post.title}
            </h3>
        </Link>
    )
}

const getHost = (event: PostEventData) => {
    const participants = event.participants
    return participants.find(p => p.role === "host")
}

const getOtherParticipants = (event: PostEventData) => {
    const participants = event.participants
    return participants.filter(p => p.role !== "host")
}


const AVATAR_SIZE = 40
const HostInfo: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const host = getHost(event)
    const instanceName = useSelector(selectInstanceName)
    const context = useContext(PersonNameContext)

    if (!host) {
        return null
    }
    const uuid = host?.avatar?.uuid

    const url = `${IMAGE_CDN_ROOT}${instanceName}/${uuid}?h=${AVATAR_SIZE}&w=${AVATAR_SIZE}&fit=facearea&facepad=50&auto=format`
    return (
        <div style={{ display: "flex", marginTop: 10, backgroundColor: '#eee', borderRadius: '20px', fontSize: '14px', fontWeight: 'bold' }}>
            <div className={styles.event_avatar}>
                <Avatar alt={host.username} src={uuid && url} />
            </div>
            <p style={{ color: "black", marginBottom: "auto", marginTop: "auto", marginLeft: 10 }}>
                {getPersonName(host, context)} <FormattedMessage id="event.hosting" />
            </p>
        </div>
    )
}

const ParticipantsAvatars: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const instanceName = useSelector(selectInstanceName)
    const other = getOtherParticipants(event)
    if (!other.length) {
        return null
    }
    return (
        <AvatarGroup max={2}>{
            other.map((p, i) => {
                const uuid = p.avatar?.uuid

                const url = `${IMAGE_CDN_ROOT}${instanceName}/${uuid}?h=${AVATAR_SIZE}&w=${AVATAR_SIZE}&fit=facearea&facepad=50&auto=format`
                return <div className={styles.event_avatar}><Avatar key={i} alt={p.username} src={uuid && url} /></div>
            })
        }</AvatarGroup>
    )
}

const ParticipantsDescription: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const context = useContext(PersonNameContext)
    const participants = event.participants
    const other = participants.filter(p => p.role !== "host")
    if (!other.length) {
        return null
    }
    return (
        <p style={{ color: "black", marginBottom: "auto", marginTop: "auto", marginLeft: 10 }}>
            {getPersonName(other[0], context)}
            {
                other.length > 1 && (<span style={{ color: "grey" }}>&nbsp;(<FormattedMessage id="event.andOthers" values={{ count: other.length - 1 }} />)</span>)
            }
            <span>&nbsp;<FormattedMessage id="event.speaking" /></span>
        </p>
    )
}

const SummaryWrapper = styled.div`
    ${({ theme }) => theme.breakpoints.down('xs')} {
        display: none;
    }
`

const EventSummary: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const { content, language } = event.post
    if (!content) {
        return null
    }
    const summary = getPostSummary(event.post)
    if (!summary) {
        return null
    }
    const dir = getDirection(language)
    return (
        <SummaryWrapper>
            <Block data={summary} dir={dir} index={1} />
        </SummaryWrapper>
    )
}

const EventParticipants: ReactFC<{ event: PostEventData }> = ({ event }) => {
    const participants = event.participants
    if (!participants.length) {
        return null
    }
    return (
        <>
            <HostInfo event={event} />
            <div className={styles.event_avatar_holder}>
                <ParticipantsAvatars event={event} />
                <ParticipantsDescription event={event} />
            </div>
        </>
    )
}

const EventCard: ReactFC<{ event: PostEventData }> = ({ event }) => {
    return (
        <div className={styles.event_list}>

            <div className={styles.event_image}>
                <EventImage event={event}>
                    <DateLayer event={event} />
                    <EventStatus event={event} />
                    <DownloadCalendarFab event={event} />
                </EventImage>
            </div>
            <div className={styles.event_content}>
                <CardContent>
                    <EventTitle event={event} />
                    <EventParticipants event={event} />
                    <EventSummary event={event} />
                    <div className={styles.event_time}>
                        <EventTime event={event} />
                    </div>
                    <EventAdditional event={event} />
                </CardContent>
            </div>
        </div>
    )


}

export default EventCard


